import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import './Visor360.css';

const Visor360 = () => {
  const { codigo, id } = useParams();
  const [recorrido, setRecorrido] = useState(null);
  const [imagenes, setImagenes] = useState([]);
  const [hotspots, setHotspots] = useState([]);
  const [imagenActual, setImagenActual] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hotspotMostrando, setHotspotMostrando] = useState(null);
  const [hotspotPosition, setHotspotPosition] = useState({ x: 0, y: 0 });
  const viewerRef = useRef(null);
  const pannellumRef = useRef(null);
  const hotspotsDataRef = useRef({}); // Almacenar datos completos de hotspots por ID
  const hotspotsCargadosRef = useRef(new Set()); // Controlar qué imágenes ya tienen hotspots cargados
  const timeoutRefs = useRef([]); // Guardar referencias de timeouts para poder limpiarlos

  useEffect(() => {
    // Cargar Pannellum desde CDN solo una vez
    if (!document.querySelector('script[src*="pannellum"]')) {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/pannellum@2.5.6/build/pannellum.js';
      script.async = true;
      document.head.appendChild(script);
    }

    if (!document.querySelector('link[href*="pannellum"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/npm/pannellum@2.5.6/build/pannellum.css';
      document.head.appendChild(link);
    }

    cargarRecorrido();

    return () => {
      if (pannellumRef.current) {
        try {
          pannellumRef.current.destroy();
        } catch (e) {
          console.log('Error al destruir visor:', e);
        }
      }
    };
  }, []);

  // Debug: Log cuando cambia el estado del tooltip
  useEffect(() => {
    if (hotspotMostrando) {
      console.log('🔔 Tooltip activado:', {
        hotspot: hotspotMostrando.nombre,
        position: hotspotPosition,
        datos: hotspotMostrando
      });
    } else {
      console.log('🔔 Tooltip desactivado');
    }
  }, [hotspotMostrando, hotspotPosition]);

  // Cerrar tooltip cuando se hace scroll o se mueve el visor
  useEffect(() => {
    if (!hotspotMostrando) return;
    
    const handleScroll = () => {
      setHotspotMostrando(null);
      setHotspotPosition({ x: 0, y: 0 });
    };
    
    const handleMouseMove = () => {
      // Cerrar tooltip cuando se mueve el mouse (opcional, puedes comentar esto si prefieres que permanezca abierto)
      // setHotspotMostrando(null);
    };
    
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('wheel', handleScroll, true);
    
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('wheel', handleScroll, true);
    };
  }, [hotspotMostrando]);

  useEffect(() => {
    // Esperar a que Pannellum y las imágenes estén listas
    const checkAndInit = () => {
      if (window.pannellum && imagenes.length > 0 && viewerRef.current && !loading) {
        // Pequeño delay para asegurar que el DOM esté listo
        setTimeout(() => {
          inicializarVisor();
        }, 100);
      }
    };

    // Verificar periódicamente si Pannellum está cargado
    const interval = setInterval(() => {
      if (window.pannellum) {
        clearInterval(interval);
        checkAndInit();
      }
    }, 100);

    checkAndInit();

    return () => clearInterval(interval);
  }, [imagenes, imagenActual, loading]);

  const cargarRecorrido = async () => {
    try {
      let response;
      if (codigo) {
        response = await api.get(`/recorridos/publico/${codigo}`);
      } else if (id) {
        response = await api.get(`/recorridos/publico/id/${id}`);
      } else {
        setError('Código o ID de recorrido requerido');
        setLoading(false);
        return;
      }

      if (response.data.success) {
        setRecorrido(response.data.recorrido);
        const imagenesRecorrido = response.data.recorrido.imagenes || [];
        setImagenes(imagenesRecorrido);
        console.log('✅ Recorrido cargado:', response.data.recorrido.titulo);
        console.log('✅ Imágenes encontradas:', imagenesRecorrido.length);
        console.log('✅ Datos de imágenes:', imagenesRecorrido);
        
        // Cargar hotspots si hay recorrido_id
        if (response.data.recorrido.id && imagenesRecorrido.length > 0) {
          cargarHotspots(response.data.recorrido.id);
        }
        
        if (imagenesRecorrido.length > 0) {
          setImagenActual(0);
        } else {
          console.warn('⚠️ El recorrido no tiene imágenes');
        }
      } else {
        setError(response.data.message || 'Error al cargar el recorrido');
      }
    } catch (err) {
      console.error('❌ Error completo al cargar recorrido:', err);
      console.error('❌ Respuesta del error:', err.response?.data);
      setError(err.response?.data?.message || 'Error al cargar el recorrido. Verifica que el recorrido exista y tenga imágenes.');
    } finally {
      setLoading(false);
    }
  };

  const inicializarVisor = () => {
    if (!window.pannellum || !viewerRef.current || imagenes.length === 0) {
      console.log('Esperando condiciones:', {
        pannellum: !!window.pannellum,
        viewerRef: !!viewerRef.current,
        imagenes: imagenes.length
      });
      return;
    }

    const imagen = imagenes[imagenActual];
    if (!imagen || !imagen.ruta_archivo) {
      console.error('Imagen inválida:', imagen);
      return;
    }

    // Destruir visor anterior si existe
    if (pannellumRef.current) {
      try {
        pannellumRef.current.destroy();
      } catch (e) {
        console.log('Error al destruir visor anterior:', e);
      }
      pannellumRef.current = null;
    }

    const apiUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
    const imagenUrl = `${apiUrl}${imagen.ruta_archivo}`;
    
    console.log('🖼️ Construyendo URL de imagen:');
    console.log('  - apiUrl:', apiUrl);
    console.log('  - ruta_archivo:', imagen.ruta_archivo);
    console.log('  - imagenUrl completa:', imagenUrl);
    console.log('Inicializando visor con imagen:', imagenUrl);

    try {
      // Limpiar el contenedor antes de inicializar
      if (viewerRef.current) {
        viewerRef.current.innerHTML = '';
      }

      // Configuración mejorada del visor - Evitar repetición al rotar
      const config = {
        type: 'equirectangular',
        panorama: imagenUrl,
        autoLoad: true,
        autoRotate: 0,
        compass: true,
        showControls: true,
        hfov: 120, // Zoom mínimo inicial (más alejado)
        pitch: 0,
        yaw: 0,
        minHfov: 30,
        maxHfov: 120,
        showFullscreenCtrl: true,
        keyboardZoom: true,
        mouseZoom: true,
        draggable: true,
        disableKeyboardCtrl: false,
        // Configuración para navegación suave y realista
        sceneFadeDuration: 3500, // Transición más lenta (3.5 segundos) para fade-in muy suave y gradual
        hotSpots: []
      };

      pannellumRef.current = window.pannellum.viewer(viewerRef.current, config);
      
      console.log('✅ Visor Pannellum creado:', !!pannellumRef.current);
      
      // Configurar límites estrictos de rotación para evitar repetición
      if (pannellumRef.current) {
        // Interceptar eventos de movimiento para limitar rotación
        let lastYaw = 0;
        let isDragging = false;
        
        // Función para limitar yaw entre -180 y 180
        const limitarYaw = (yaw) => {
          let limited = yaw;
          // Normalizar entre -180 y 180
          while (limited > 180) limited -= 360;
          while (limited < -180) limited += 360;
          return limited;
        };

        // Interceptar eventos de mouse
        const viewerElement = viewerRef.current;
        if (viewerElement) {
          viewerElement.addEventListener('mousedown', (e) => {
            isDragging = true;
            lastYaw = pannellumRef.current.getYaw();
          });

          viewerElement.addEventListener('mousemove', (e) => {
            if (isDragging && pannellumRef.current) {
              const currentYaw = pannellumRef.current.getYaw();
              const limitedYaw = limitarYaw(currentYaw);
              
              // Si el yaw se salió del rango, corregirlo inmediatamente
              if (Math.abs(currentYaw - limitedYaw) > 1) {
                pannellumRef.current.setYaw(limitedYaw);
              }
              lastYaw = limitedYaw;
            }
          });

          viewerElement.addEventListener('mouseup', () => {
            isDragging = false;
            if (pannellumRef.current) {
              const currentYaw = pannellumRef.current.getYaw();
              const limitedYaw = limitarYaw(currentYaw);
              pannellumRef.current.setYaw(limitedYaw);
            }
          });

          // También interceptar eventos táctiles para móviles
          viewerElement.addEventListener('touchstart', () => {
            isDragging = true;
            lastYaw = pannellumRef.current.getYaw();
          });

          viewerElement.addEventListener('touchmove', () => {
            if (isDragging && pannellumRef.current) {
              const currentYaw = pannellumRef.current.getYaw();
              const limitedYaw = limitarYaw(currentYaw);
              if (Math.abs(currentYaw - limitedYaw) > 1) {
                pannellumRef.current.setYaw(limitedYaw);
              }
            }
          });

          viewerElement.addEventListener('touchend', () => {
            isDragging = false;
            if (pannellumRef.current) {
              const currentYaw = pannellumRef.current.getYaw();
              const limitedYaw = limitarYaw(currentYaw);
              pannellumRef.current.setYaw(limitedYaw);
            }
          });
        }

        // Monitoreo continuo con intervalo más frecuente
        const checkInterval = setInterval(() => {
          if (pannellumRef.current) {
            try {
              const currentYaw = pannellumRef.current.getYaw();
              const limitedYaw = limitarYaw(currentYaw);
              
              // Si se sale del rango, corregir inmediatamente
              if (Math.abs(currentYaw - limitedYaw) > 0.5) {
                pannellumRef.current.setYaw(limitedYaw);
              }
            } catch (e) {
              // Ignorar errores durante el monitoreo
            }
          }
        }, 16); // ~60fps para corrección suave

        // También usar el evento 'animate' de Pannellum
        pannellumRef.current.on('animate', () => {
          if (pannellumRef.current) {
            try {
              const currentYaw = pannellumRef.current.getYaw();
              const limitedYaw = limitarYaw(currentYaw);
              // Corregir inmediatamente si se sale del rango
              if (Math.abs(currentYaw - limitedYaw) > 0.1) {
                pannellumRef.current.setYaw(limitedYaw);
              }
            } catch (e) {
              // Ignorar errores
            }
          }
        });

        // Interceptar el evento de renderizado para forzar límites
        pannellumRef.current.on('render', () => {
          if (pannellumRef.current) {
            try {
              const currentYaw = pannellumRef.current.getYaw();
              const limitedYaw = limitarYaw(currentYaw);
              if (Math.abs(currentYaw - limitedYaw) > 0.1) {
                pannellumRef.current.setYaw(limitedYaw, 0); // Sin animación para corrección inmediata
              }
            } catch (e) {
              // Ignorar errores
            }
          }
        });

        // Guardar el intervalo para limpiarlo después si es necesario
        // Nota: El cleanup se hace en el useEffect, no aquí
      }
      
      // Cargar hotspots de la imagen actual después de inicializar
      console.log('🔍 Verificando si hay imagen.id:', imagen.id);
      if (imagen.id) {
        // Limpiar timeouts anteriores si existen
        timeoutRefs.current.forEach(timeout => clearTimeout(timeout));
        timeoutRefs.current = [];
        
        // Verificar si ya se cargaron hotspots para esta imagen
        const imagenKey = `imagen_${imagen.id}`;
        if (hotspotsCargadosRef.current.has(imagenKey)) {
          console.log(`✅ Hotspots ya cargados para imagen ${imagen.id}, omitiendo...`);
        } else {
          console.log(`🔄 Programando carga de hotspots para imagen ${imagen.id}...`);
          
          // Función para cargar hotspots (solo una vez por imagen)
          const cargarHotspotsDeImagen = () => {
            if (hotspotsCargadosRef.current.has(imagenKey)) {
              console.log(`⏭️ Hotspots ya cargados para imagen ${imagen.id}, omitiendo...`);
              return;
            }
            
            console.log('🔍 cargarHotspotsDeImagen llamada');
            if (!pannellumRef.current) {
              console.warn('⚠️ pannellumRef.current no está disponible para cargar hotspots');
              return;
            }
            
            try {
              const config = pannellumRef.current.getConfig();
              console.log('🔍 Configuración del visor obtenida:', {
                tieneConfig: !!config,
                tienePanorama: !!config?.panorama,
                panorama: config?.panorama
              });
              
              if (config && config.panorama) {
                console.log(`✅ Visor listo, cargando hotspots para imagen ${imagen.id}...`);
                hotspotsCargadosRef.current.add(imagenKey);
                cargarHotspotsPorImagen(imagen.id);
              } else {
                console.warn('⚠️ Visor no tiene panorama configurado aún');
              }
            } catch (e) {
              console.error('❌ Error al verificar configuración del visor:', e);
            }
          };
          
          // Escuchar el evento 'load' del visor (solo una vez)
          console.log('🔍 Configurando listener para evento load...');
          const loadHandler = () => {
            console.log('📸 Evento load disparado en Visor360 para imagen:', imagen.id);
            const timeout = setTimeout(() => {
              cargarHotspotsDeImagen();
              pannellumRef.current.off('load', loadHandler);
            }, 1000);
            timeoutRefs.current.push(timeout);
          };
          pannellumRef.current.on('load', loadHandler);
          
          // También intentar cargar después de un delay (solo una vez)
          console.log('🔍 Configurando timeout único para cargar hotspots...');
          const timeout1 = setTimeout(() => {
            console.log('📸 Intentando cargar hotspots (delay 2s)...');
            cargarHotspotsDeImagen();
          }, 2000);
          timeoutRefs.current.push(timeout1);
        }
      } else {
        console.warn('⚠️ No hay imagen.id para cargar hotspots');
      }

      // Manejar cambio de escena cuando se hace clic en un hotspot de navegación
      if (pannellumRef.current && imagenes.length > 1) {
        // Remover listener anterior si existe para evitar duplicados
        pannellumRef.current.off('scenechange');
        
        pannellumRef.current.on('scenechange', (sceneId) => {
          console.log('🔄 Cambio de escena detectado, sceneId:', sceneId);
          
          let imagenIndex = -1;
          
          // Manejar cuando sceneId es un objeto (cuando se usa loadScene con objeto de configuración)
          if (typeof sceneId === 'object' && sceneId !== null) {
            // Buscar la imagen por su URL
            const panoramaUrl = sceneId.panorama;
            if (panoramaUrl) {
              imagenIndex = imagenes.findIndex(img => {
                const apiUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
                const imgUrl = `${apiUrl}${img.ruta_archivo}`;
                return imgUrl === panoramaUrl;
              });
            }
          } else if (typeof sceneId === 'string') {
            // Manejar cuando sceneId es un string (formato: "imagen_0", "imagen_1", etc.)
            imagenIndex = parseInt(sceneId.replace('imagen_', ''));
          }
          
          console.log('🔄 Índice de imagen extraído:', imagenIndex);
          
          if (!isNaN(imagenIndex) && imagenIndex >= 0 && imagenIndex < imagenes.length) {
            console.log('✅ Cambiando a imagen:', imagenIndex, 'Título:', imagenes[imagenIndex]?.nombre || imagenes[imagenIndex]?.ruta_archivo);
            cambiarImagen(imagenIndex);
          } else {
            console.warn('⚠️ Índice de imagen inválido:', imagenIndex, 'Total de imágenes:', imagenes.length);
          }
        });
      }

      // Manejar clicks en hotspots informativos (solo configurar una vez)
      if (pannellumRef.current) {
        // Remover listener anterior si existe para evitar duplicados
        pannellumRef.current.off('hotspotclick');
        
        pannellumRef.current.on('hotspotclick', (hotspotId, event) => {
          console.log('🖱️ Click en hotspot - ID recibido:', hotspotId);
          console.log('🖱️ Tipo de hotspotId:', typeof hotspotId);
          console.log('🖱️ Evento completo:', event);
          console.log('🔍 Hotspots disponibles en ref:', Object.keys(hotspotsDataRef.current));
          
          // Buscar el hotspot en los datos almacenados
          let hotspotData = null;
          
          // Intentar múltiples formas de encontrar el hotspot
          if (hotspotId && typeof hotspotId === 'string') {
            hotspotData = hotspotsDataRef.current[hotspotId];
            if (!hotspotData && hotspotId.startsWith('hotspot-')) {
              // Ya tiene el prefijo
            } else if (!hotspotData) {
              hotspotData = hotspotsDataRef.current[`hotspot-${hotspotId}`];
            }
          } else if (hotspotId && typeof hotspotId === 'number') {
            hotspotData = hotspotsDataRef.current[`hotspot-${hotspotId}`];
          }
          
          // Si no se encuentra, buscar por índice en la configuración del visor
          if (!hotspotData && pannellumRef.current) {
            try {
              const config = pannellumRef.current.getConfig();
              if (config && config.hotSpots && Array.isArray(config.hotSpots)) {
                const hotspotIndex = typeof hotspotId === 'number' ? hotspotId : parseInt(hotspotId);
                if (!isNaN(hotspotIndex) && config.hotSpots[hotspotIndex]) {
                  const hotspotConfig = config.hotSpots[hotspotIndex];
                  const configId = hotspotConfig.id || hotspotConfig.text;
                  
                  if (configId) {
                    hotspotData = hotspotsDataRef.current[configId];
                    if (!hotspotData && typeof configId === 'string') {
                      if (configId.startsWith('hotspot-')) {
                        hotspotData = hotspotsDataRef.current[configId];
                      } else {
                        hotspotData = hotspotsDataRef.current[`hotspot-${configId}`];
                      }
                    }
                  }
                  
                  // Si aún no se encuentra, buscar por ID numérico extraído del configId
                  if (!hotspotData && configId) {
                    const idMatch = String(configId).match(/(\d+)/);
                    if (idMatch) {
                      const hotspotIdNum = parseInt(idMatch[1]);
                      Object.values(hotspotsDataRef.current).forEach(hs => {
                        if (hs && hs.id === hotspotIdNum) {
                          hotspotData = hs;
                        }
                      });
                    }
                  }
                }
              }
            } catch (e) {
              console.error('❌ Error al buscar hotspot en configuración:', e);
            }
          }
          
          // Si es un hotspot de navegación, NO interceptar el clic - dejar que Pannellum maneje la navegación
          if (hotspotData && hotspotData.tipo === 'navegacion') {
            console.log('🚀 Hotspot de navegación detectado, permitiendo navegación automática');
            return; // No hacer nada, dejar que Pannellum maneje la navegación
          }
          
          // Solo prevenir comportamiento por defecto para hotspots informativos
          if (hotspotData && hotspotData.tipo !== 'navegacion') {
            if (event && event.preventDefault) {
              event.preventDefault();
            }
            if (event && event.stopPropagation) {
              event.stopPropagation();
            }
          }
          
          // Si encontramos el hotspot y es informativo, mostrar el tooltip
          if (hotspotData && hotspotData.tipo !== 'navegacion') {
            console.log('✅ Datos del hotspot encontrados:', hotspotData);
            // Obtener posición del elemento del hotspot en pantalla
            const hotspotElement = document.querySelector(`[data-hotspot-id="${hotspotId}"]`) ||
                                 document.querySelector(`[data-hotspot-id="hotspot-${hotspotData.id}"]`);
            if (hotspotElement) {
              const rect = hotspotElement.getBoundingClientRect();
              setHotspotPosition({
                x: rect.left + rect.width / 2,
                y: rect.top
              });
            }
            setHotspotMostrando(hotspotData);
          } else if (!hotspotData) {
            console.warn('⚠️ No se encontraron datos para el hotspot:', hotspotId);
            console.warn('⚠️ Hotspots disponibles:', Object.keys(hotspotsDataRef.current));
            
            // Último intento: buscar en el DOM
            setTimeout(() => {
              const hotspotElements = document.querySelectorAll('[data-hotspot-id]');
              console.log('🔍 Elementos DOM con data-hotspot-id:', hotspotElements.length);
              hotspotElements.forEach(el => {
                const domId = el.getAttribute('data-hotspot-id');
                console.log('🔍 ID en DOM:', domId);
                if (domId && hotspotsDataRef.current[domId]) {
                  const hotspotEncontrado = hotspotsDataRef.current[domId];
                  // Solo mostrar tooltip si es informativo
                  if (hotspotEncontrado.tipo !== 'navegacion') {
                    console.log('✅ Encontrado en DOM:', hotspotEncontrado);
                    const rect = el.getBoundingClientRect();
                    setHotspotPosition({
                      x: rect.left + rect.width / 2,
                      y: rect.top
                    });
                    setHotspotMostrando(hotspotEncontrado);
                  }
                }
              });
            }, 100);
          }
        });
      }

      console.log('✅ Visor inicializado correctamente');
      console.log('🔍 Estado después de inicializar:', {
        pannellumRefExiste: !!pannellumRef.current,
        imagenId: imagen.id,
        imagenActual: imagenActual
      });
    } catch (err) {
      console.error('❌ Error al inicializar visor:', err);
      console.error('Stack:', err.stack);
      setError('Error al cargar el visor 360º. Verifica que la imagen sea válida.');
    }
  };

  const cargarHotspots = async (recorridoId) => {
    try {
      const response = await api.get(`/hotspots/recorrido/${recorridoId}`);
      if (response.data.success) {
        const hotspotsRecorrido = response.data.hotspots || [];
        setHotspots(hotspotsRecorrido);
        console.log('✅ Hotspots del recorrido cargados:', hotspotsRecorrido.length);
        
        // Si hay una imagen actual, cargar sus hotspots específicos
        if (imagenes.length > 0 && imagenActual >= 0 && imagenes[imagenActual]?.id) {
          console.log(`🔄 Cargando hotspots para imagen actual (${imagenes[imagenActual].id})...`);
          setTimeout(() => {
            cargarHotspotsPorImagen(imagenes[imagenActual].id);
          }, 500);
        }
      }
    } catch (err) {
      console.log('⚠️ No se pudieron cargar hotspots (puede ser normal si no hay)');
    }
  };

  const cambiarImagen = (index) => {
    if (index >= 0 && index < imagenes.length) {
      setImagenActual(index);
      setHotspotMostrando(null); // Cerrar tooltip si está abierto
      setHotspotPosition({ x: 0, y: 0 });
      
      // Limpiar timeouts anteriores
      timeoutRefs.current.forEach(timeout => clearTimeout(timeout));
      timeoutRefs.current = [];
      
      // Limpiar hotspots anteriores antes de cargar nuevos
      if (pannellumRef.current) {
        try {
          const config = pannellumRef.current.getConfig();
          if (config && config.hotSpots && Array.isArray(config.hotSpots)) {
            // Remover hotspots en orden inverso
            for (let i = config.hotSpots.length - 1; i >= 0; i--) {
              try {
                pannellumRef.current.removeHotSpot(i);
              } catch (e) {
                if (config.hotSpots[i]?.id) {
                  try {
                    pannellumRef.current.removeHotSpot(config.hotSpots[i].id);
                  } catch (e2) {}
                }
              }
            }
          }
        } catch (e) {
          console.warn('⚠️ Error al limpiar hotspots:', e);
        }
      }
      
      // Limpiar referencia de datos de hotspots anteriores y marca de carga
      hotspotsDataRef.current = {};
      hotspotsCargadosRef.current.clear();
      
      // Cargar hotspots de la nueva imagen después de que el visor se actualice
      if (pannellumRef.current && imagenes[index] && imagenes[index].id) {
        const imagenKey = `imagen_${imagenes[index].id}`;
        
        // Escuchar el evento de cambio de escena
        const handleSceneChange = () => {
          setTimeout(() => {
            console.log(`🔄 Cambio de escena detectado, cargando hotspots para imagen ${imagenes[index].id}...`);
            if (!hotspotsCargadosRef.current.has(imagenKey)) {
              hotspotsCargadosRef.current.add(imagenKey);
              cargarHotspotsPorImagen(imagenes[index].id);
            }
            pannellumRef.current.off('scenechange', handleSceneChange);
          }, 500);
        };
        
        pannellumRef.current.on('scenechange', handleSceneChange);
        
        // También intentar cargar después de un delay por si el evento no se dispara
        const timeout = setTimeout(() => {
          if (pannellumRef.current && imagenes[index] && imagenes[index].id) {
            if (!hotspotsCargadosRef.current.has(imagenKey)) {
              console.log(`🔄 Cargando hotspots para imagen ${imagenes[index].id} (después de delay)...`);
              hotspotsCargadosRef.current.add(imagenKey);
              cargarHotspotsPorImagen(imagenes[index].id);
            }
          }
        }, 1000);
        timeoutRefs.current.push(timeout);
      }
    }
  };

  const cargarHotspotsPorImagen = async (imagenId) => {
    try {
      console.log(`🔄 Cargando hotspots para imagen ${imagenId}...`);
      const response = await api.get(`/hotspots/imagen/${imagenId}`);
      if (response.data.success) {
        const hotspotsImagen = response.data.hotspots || [];
        console.log(`✅ Hotspots recibidos del servidor para imagen ${imagenId}:`, hotspotsImagen.length);
        hotspotsImagen.forEach((hs, idx) => {
          console.log(`  Hotspot ${idx + 1}:`, {
            id: hs.id,
            nombre: hs.nombre,
            pitch: hs.pitch,
            yaw: hs.yaw,
            tipo: hs.tipo
          });
        });
        
        // Actualizar visor con hotspots (incluso si está vacío para limpiar)
        console.log(`🔍 Verificando pannellumRef antes de actualizar hotspots:`, {
          pannellumRefExiste: !!pannellumRef.current,
          hotspotsImagenLength: hotspotsImagen.length
        });
        
        if (pannellumRef.current) {
          console.log(`✅ Llamando actualizarHotspotsEnVisor con ${hotspotsImagen.length} hotspots`);
          actualizarHotspotsEnVisor(hotspotsImagen);
        } else {
          console.warn('⚠️ Pannellum no está disponible para agregar hotspots');
          console.warn('   Esto puede significar que el visor aún no se ha inicializado');
          console.warn('   Intentando de nuevo en 1 segundo...');
          setTimeout(() => {
            if (pannellumRef.current) {
              console.log(`✅ Reintentando actualizarHotspotsEnVisor con ${hotspotsImagen.length} hotspots`);
              actualizarHotspotsEnVisor(hotspotsImagen);
            } else {
              console.error('❌ Pannellum aún no está disponible después del reintento');
            }
          }, 1000);
        }
      } else {
        console.warn('⚠️ Respuesta del servidor no fue exitosa:', response.data);
        // Aún así, limpiar hotspots si no hay para esta imagen
        if (pannellumRef.current) {
          actualizarHotspotsEnVisor([]);
        }
      }
    } catch (err) {
      console.error('❌ Error al cargar hotspots de la imagen:', err);
      console.error('Detalles:', err.response?.data || err.message);
      // Aún así, limpiar hotspots si hay error
      if (pannellumRef.current) {
        try {
          actualizarHotspotsEnVisor([]);
        } catch (e) {
          console.warn('⚠️ Error al limpiar hotspots:', e);
        }
      }
    }
  };

  const actualizarHotspotsEnVisor = (hotspotsImagen) => {
    console.log(`🔍 actualizarHotspotsEnVisor llamado con ${hotspotsImagen.length} hotspots`);
    console.log(`🔍 pannellumRef.current existe:`, !!pannellumRef.current);
    console.log(`🔍 window.pannellum existe:`, !!window.pannellum);
    
    if (!pannellumRef.current || !window.pannellum) {
      console.warn('⚠️ Pannellum no está listo para agregar hotspots');
      console.warn('   pannellumRef.current:', pannellumRef.current);
      console.warn('   window.pannellum:', window.pannellum);
      return;
    }
    
    console.log(`🔄 Actualizando hotspots en Visor360. Total: ${hotspotsImagen.length}`);
    
    try {
      // Limpiar hotspots anteriores de forma más segura
      const currentConfig = pannellumRef.current.getConfig();
      if (currentConfig.hotSpots && Array.isArray(currentConfig.hotSpots)) {
        console.log(`Limpiando ${currentConfig.hotSpots.length} hotspots existentes en Visor360...`);
        // Remover en orden inverso para evitar problemas con índices
        for (let i = currentConfig.hotSpots.length - 1; i >= 0; i--) {
          try {
            pannellumRef.current.removeHotSpot(i);
          } catch (e) {
            // Intentar por ID si está disponible
            if (currentConfig.hotSpots[i]?.id) {
              try {
                pannellumRef.current.removeHotSpot(currentConfig.hotSpots[i].id);
              } catch (e2) {
                // Ignorar errores al remover
              }
            }
          }
        }
      }

      // Esperar un momento antes de agregar nuevos hotspots para asegurar que la limpieza se complete
      setTimeout(() => {
        if (!pannellumRef.current) {
          console.warn('⚠️ Pannellum ya no está disponible');
          return;
        }
        
        // Verificar que la limpieza se completó
        const configDespuesLimpieza = pannellumRef.current.getConfig();
        const hotspotsRestantes = configDespuesLimpieza.hotSpots?.length || 0;
        if (hotspotsRestantes > 0) {
          console.warn(`⚠️ Aún quedan ${hotspotsRestantes} hotspots después de limpiar. Limpiando de nuevo...`);
          // Limpiar de nuevo
          for (let i = configDespuesLimpieza.hotSpots.length - 1; i >= 0; i--) {
            try {
              pannellumRef.current.removeHotSpot(i);
            } catch (e) {
              if (configDespuesLimpieza.hotSpots[i]?.id) {
                try {
                  pannellumRef.current.removeHotSpot(configDespuesLimpieza.hotSpots[i].id);
                } catch (e2) {}
              }
            }
          }
        }
        
        console.log(`✅ Agregando ${hotspotsImagen.length} hotspots en Visor360...`);
        
        // Agregar nuevos hotspots uno por uno con delay
        hotspotsImagen.forEach((hotspot, index) => {
          setTimeout(() => {
            if (!pannellumRef.current) return;
            
            try {
              const pitch = parseFloat(hotspot.pitch);
              const yaw = parseFloat(hotspot.yaw);
              
              if (isNaN(pitch) || isNaN(yaw)) {
                console.error('❌ Coordenadas inválidas para hotspot:', hotspot);
                return;
              }
              
              console.log(`Agregando hotspot "${hotspot.nombre}" en Visor360:`, { pitch, yaw, tipo: hotspot.tipo });
              
              // Normalizar tipo de hotspot (puede venir como 'informativo' o 'info' del servidor)
              const tipoNormalizado = hotspot.tipo === 'navegacion' ? 'navegacion' : 
                                     (hotspot.tipo === 'informativo' || hotspot.tipo === 'info') ? 'info' : 'info';
              
              // Determinar el tipo de hotspot
              const esNavegacion = (hotspot.tipo === 'navegacion' || tipoNormalizado === 'navegacion') && hotspot.imagen_destino_id;
              
              const hotspotId = `hotspot-${hotspot.id}`;
              
              // Guardar los datos completos del hotspot para poder mostrarlos después
              hotspotsDataRef.current[hotspotId] = hotspot;
              
              const hotspotConfig = {
                pitch: pitch,
                yaw: yaw,
                type: esNavegacion ? 'scene' : 'info',
                text: hotspot.nombre || 'Punto de interés',
                cssClass: 'custom-hotspot', // Forzar clase personalizada para visibilidad
                id: hotspotId, // Agregar ID único
                // Asegurar que el hotspot sea visible usando createTooltipFunc
                createTooltipFunc: function(hotSpotDiv, args) {
                  // Asegurar que el div del hotspot sea visible
                  hotSpotDiv.style.opacity = '1';
                  hotSpotDiv.style.visibility = 'visible';
                  hotSpotDiv.style.display = 'block';
                  hotSpotDiv.style.backgroundColor = esNavegacion ? 'rgba(40, 167, 69, 0.9)' : 'rgba(0, 123, 255, 0.9)';
                  hotSpotDiv.style.border = '3px solid #ffffff';
                  hotSpotDiv.style.borderRadius = '50%';
                  hotSpotDiv.style.width = '40px';
                  hotSpotDiv.style.height = '40px';
                  hotSpotDiv.style.boxShadow = `0 0 10px ${esNavegacion ? 'rgba(40, 167, 69, 0.8)' : 'rgba(0, 123, 255, 0.8)'}`;
                  hotSpotDiv.setAttribute('data-hotspot-id', hotspotId);
                }
              };

              if ((hotspot.tipo === 'navegacion' || tipoNormalizado === 'navegacion') && hotspot.imagen_destino_id) {
                // Encontrar índice de la imagen destino
                const destinoIndex = imagenes.findIndex(img => img.id === hotspot.imagen_destino_id);
                if (destinoIndex !== -1) {
                  const imagenDestino = imagenes[destinoIndex];
                  const apiUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
                  const imagenDestinoUrl = `${apiUrl}${imagenDestino.ruta_archivo}`;
                  
                  console.log('🚀 Configurando hotspot de navegación:', {
                    hotspotId: hotspot.id,
                    destinoIndex,
                    imagenDestinoUrl,
                    imagenDestinoId: imagenDestino.id
                  });
                  
                  // Configurar como hotspot de tipo 'scene' pero usar clickHandlerFunc para navegar manualmente
                  // Esto evita problemas con escenas no configuradas
                  hotspotConfig.type = 'info'; // Cambiar a 'info' para tener control total del clic
                  hotspotConfig.clickHandlerFunc = function() {
                    console.log('🚀 Click en hotspot de navegación:', hotspot.id);
                    console.log('🚀 Navegando a imagen destino:', destinoIndex, imagenDestinoUrl);
                    
                    if (pannellumRef.current) {
                      // Obtener posición actual de la cámara
                      const currentPitch = pannellumRef.current.getPitch();
                      const currentYaw = pannellumRef.current.getYaw();
                      const currentHfov = pannellumRef.current.getHfov();
                      
                      // Animación suave y realista: simular caminar hacia la puerta/pasillo
                      const targetPitch = parseFloat(hotspot.pitch);
                      const targetYaw = parseFloat(hotspot.yaw);
                      
                      // Paso 1: Animar hacia el hotspot con zoom progresivo (simula acercarse)
                      // Usar una animación más lenta y suave (1200ms) para efecto más realista
                      pannellumRef.current.lookAt(targetPitch, targetYaw, 55, 1200, () => {
                        // Paso 2: Pequeña pausa en el hotspot antes de cambiar (como si estuvieras en la puerta)
                        setTimeout(() => {
                          // Paso 3: Cambiar a la nueva escena con transición muy suave y larga
                          // El segundo parámetro de loadScene es la duración del fade (3500ms = 3.5 segundos)
                          pannellumRef.current.loadScene({
                            type: 'equirectangular',
                            panorama: imagenDestinoUrl,
                            pitch: 0, // Empezar desde el centro en la nueva imagen
                            yaw: 0,
                            hfov: 80
                          }, 3500); // Duración de la transición (3.5 segundos para fade-in muy suave y gradual)
                          
                          // Actualizar el estado de React para cambiar la imagen actual
                          // Esperar más tiempo para que la transición visual se complete completamente
                          setTimeout(() => {
                            cambiarImagen(destinoIndex);
                          }, 500);
                        }, 400); // Pausa de 400ms en el hotspot (como estar en la puerta)
                      });
                    }
                  };
                } else {
                  console.warn('⚠️ No se encontró imagen destino para hotspot de navegación:', hotspot.imagen_destino_id);
                }
              } else if (hotspot.url_externa) {
                hotspotConfig.URL = hotspot.url_externa;
              } else if (hotspot.descripcion) {
                hotspotConfig.text = `${hotspot.nombre || 'Punto de interés'}\n${hotspot.descripcion}`;
              }

              // Verificar que el visor esté listo antes de agregar
              if (!pannellumRef.current) {
                console.error('❌ Pannellum no está disponible al intentar agregar hotspot');
                return;
              }
              
              // Verificar que el visor tenga una configuración válida
              try {
                const configCheck = pannellumRef.current.getConfig();
                if (!configCheck || !configCheck.panorama) {
                  console.warn(`⚠️ Visor no tiene panorama configurado al agregar hotspot ${hotspot.id}`);
                  return;
                }
              } catch (configError) {
                console.error(`❌ Error al verificar configuración antes de agregar hotspot ${hotspot.id}:`, configError);
                return;
              }
              
              console.log(`🔍 Intentando agregar hotspot "${hotspot.nombre}" con configuración:`, hotspotConfig);
              
              try {
                const result = pannellumRef.current.addHotSpot(hotspotConfig);
                console.log('✅ Hotspot agregado en Visor360:', hotspot.nombre, 'en coordenadas:', { pitch, yaw }, 'resultado:', result);
                
                // Forzar visibilidad del hotspot después de agregarlo y agregar listener de click directo
                setTimeout(() => {
                  if (pannellumRef.current) {
                    try {
                      const hotspotElement = document.querySelector(`[data-hotspot-id="hotspot-${hotspot.id}"]`) ||
                                           document.querySelector(`.pnlm-hotspot[id*="hotspot-${hotspot.id}"]`) ||
                                           document.querySelector(`#hotspot-${hotspot.id}`);
                      
                      if (hotspotElement) {
                        hotspotElement.style.opacity = '1';
                        hotspotElement.style.visibility = 'visible';
                        hotspotElement.style.display = 'block';
                        hotspotElement.style.cursor = 'pointer';
                        console.log(`✅ Visibilidad forzada para hotspot ${hotspot.id}`);
                        
                        // Agregar listener de click directo en el DOM como respaldo
                        const clickHandler = (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('🖱️ Click directo en DOM para hotspot:', hotspot.id);
                          console.log('🖱️ Datos del hotspot:', hotspot);
                          
                          // Obtener posición del elemento para mostrar el tooltip
                          const rect = hotspotElement.getBoundingClientRect();
                          
                          // Calcular posición centrada en el hotspot
                          let x = rect.left + rect.width / 2;
                          let y = rect.top;
                          
                          // Asegurar que el tooltip esté dentro de los límites de la ventana
                          const tooltipWidth = 320; // max-width del tooltip
                          const tooltipHeight = 200; // altura estimada del tooltip
                          
                          // Ajustar horizontalmente si está fuera de los límites
                          if (x < tooltipWidth / 2) {
                            x = tooltipWidth / 2 + 10;
                          } else if (x > window.innerWidth - tooltipWidth / 2) {
                            x = window.innerWidth - tooltipWidth / 2 - 10;
                          }
                          
                          // Ajustar verticalmente si está fuera de los límites
                          if (y < tooltipHeight + 20) {
                            y = rect.bottom + 20; // Mostrar debajo del hotspot
                          }
                          
                          const position = { x, y };
                          
                          console.log('📍 Posición calculada del hotspot:', position);
                          console.log('📍 Rect del elemento:', rect);
                          console.log('📍 Dimensiones de ventana:', { width: window.innerWidth, height: window.innerHeight });
                          
                          setHotspotPosition(position);
                          setHotspotMostrando(hotspot);
                          
                          console.log('✅ Tooltip configurado para mostrar:', hotspot.nombre);
                        };
                        
                        // Remover listener anterior si existe
                        hotspotElement.removeEventListener('click', clickHandler);
                        hotspotElement.addEventListener('click', clickHandler, true);
                        console.log(`✅ Listener de click agregado directamente al DOM para hotspot ${hotspot.id}`);
                      } else {
                        // Buscar por clase y coordenadas
                        const allHotspots = document.querySelectorAll('.pnlm-hotspot-base, .pnlm-hotspot');
                        console.log(`🔍 Buscando hotspot ${hotspot.id} entre ${allHotspots.length} elementos...`);
                        
                        // Intentar encontrar el hotspot por posición o atributos
                        allHotspots.forEach((el, idx) => {
                          // Si no tiene data-hotspot-id, intentar agregarlo basándose en el índice
                          if (!el.getAttribute('data-hotspot-id')) {
                            const config = pannellumRef.current.getConfig();
                            if (config && config.hotSpots && config.hotSpots[idx]) {
                              const configHotspot = config.hotSpots[idx];
                              if (configHotspot.id === `hotspot-${hotspot.id}`) {
                                el.setAttribute('data-hotspot-id', `hotspot-${hotspot.id}`);
                                el.style.cursor = 'pointer';
                                const clickHandler = (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  console.log('🖱️ Click directo en DOM (por índice) para hotspot:', hotspot.id);
                                  
                                  // Obtener posición del elemento
                                  const rect = el.getBoundingClientRect();
                                  setHotspotPosition({
                                    x: rect.left + rect.width / 2,
                                    y: rect.top
                                  });
                                  
                                  setHotspotMostrando(hotspot);
                                };
                                el.removeEventListener('click', clickHandler);
                                el.addEventListener('click', clickHandler, true);
                                console.log(`✅ Listener agregado por índice para hotspot ${hotspot.id}`);
                              }
                            }
                          }
                        });
                      }
                    } catch (e) {
                      console.warn('⚠️ Error al forzar visibilidad:', e);
                    }
                  }
                }, 200);
                
                // Verificar que se agregó correctamente después de un delay
                setTimeout(() => {
                  if (!pannellumRef.current) return;
                  const config = pannellumRef.current.getConfig();
                  const addedHotspot = config?.hotSpots?.find(hs => hs.id === `hotspot-${hotspot.id}`);
                  if (addedHotspot) {
                    const addedPitch = parseFloat(addedHotspot.pitch);
                    const addedYaw = parseFloat(addedHotspot.yaw);
                    console.log(`✅ Verificado: Hotspot ${hotspot.id} "${hotspot.nombre}" está en el visor con coordenadas:`, {
                      pitch: addedPitch,
                      yaw: addedYaw,
                      pitchEsperado: pitch,
                      yawEsperado: yaw
                    });
                    
                    // Verificar que las coordenadas coinciden
                    if (Math.abs(addedPitch - pitch) > 0.1 || Math.abs(addedYaw - yaw) > 0.1) {
                      console.warn(`⚠️ Las coordenadas del hotspot ${hotspot.id} no coinciden!`);
                    }
                  } else {
                    console.warn(`⚠️ Hotspot ${hotspot.id} no se encontró después de agregar`);
                    console.warn(`   Hotspots disponibles:`, config?.hotSpots?.map(hs => ({ id: hs.id, pitch: hs.pitch, yaw: hs.yaw })));
                  }
                }, 200);
              } catch (addError) {
                console.error(`❌ Error al llamar addHotSpot para "${hotspot.nombre}":`, addError);
                console.error('   Configuración:', hotspotConfig);
                console.error('   Hotspot:', hotspot);
              }
            } catch (e) {
              console.error('❌ Error al agregar hotspot:', e);
              console.error('   Hotspot:', hotspot);
            }
          }, index * 100); // Delay escalonado para cada hotspot (aumentado a 100ms)
        });
        
        // Verificación final después de agregar todos
        setTimeout(() => {
          if (pannellumRef.current) {
            const finalConfig = pannellumRef.current.getConfig();
            const finalHotspots = finalConfig.hotSpots || [];
            console.log(`📊 Hotspots finales en Visor360: ${finalHotspots.length}`);
            finalHotspots.forEach((hs, idx) => {
              console.log(`  Hotspot ${idx + 1}:`, {
                id: hs.id,
                pitch: hs.pitch,
                yaw: hs.yaw,
                text: hs.text,
                type: hs.type,
                cssClass: hs.cssClass
              });
            });
            
            // Forzar visibilidad de todos los hotspots en el DOM
            const hotspotElements = document.querySelectorAll('.pnlm-hotspot-base, .pnlm-hotspot');
            console.log(`🔍 Elementos DOM de hotspots encontrados: ${hotspotElements.length}`);
            hotspotElements.forEach((el, idx) => {
              el.style.opacity = '1';
              el.style.visibility = 'visible';
              el.style.display = 'block';
              console.log(`  ✅ Hotspot DOM ${idx + 1} forzado a visible:`, {
                id: el.id,
                className: el.className,
                style: {
                  opacity: el.style.opacity,
                  visibility: el.style.visibility,
                  display: el.style.display
                }
              });
            });
            
            if (hotspotElements.length === 0 && finalHotspots.length > 0) {
              console.warn('⚠️ Hay hotspots en la configuración pero no se encontraron elementos DOM');
              console.warn('   Esto puede indicar que los hotspots aún no se han renderizado');
              console.warn('   Intentando forzar renderizado...');
              
              // Intentar forzar un redraw del visor
              try {
                const currentPitch = pannellumRef.current.getPitch();
                const currentYaw = pannellumRef.current.getYaw();
                pannellumRef.current.setPitch(currentPitch + 0.01);
                setTimeout(() => {
                  pannellumRef.current.setPitch(currentPitch);
                  pannellumRef.current.setYaw(currentYaw);
                }, 100);
              } catch (e) {
                console.warn('⚠️ Error al forzar redraw:', e);
              }
            }
          }
        }, hotspotsImagen.length * 100 + 800);
      }, 300);
    } catch (e) {
      console.error('Error al actualizar hotspots:', e);
    }
  };

  const descargarCodigo = () => {
    const codigoEmbed = `<iframe 
  src="${window.location.origin}/visor/${codigo || id}" 
  width="100%" 
  height="600px" 
  frameborder="0" 
  allowfullscreen>
</iframe>`;

    const blob = new Blob([codigoEmbed], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'codigo-embed.html';
    a.click();
    URL.revokeObjectURL(url);
  };

  const copiarEnlace = () => {
    navigator.clipboard.writeText(window.location.href);
    alert('Enlace copiado al portapapeles');
  };

  if (loading) {
    return (
      <div className="main-content">
        <div className="loading">Cargando recorrido...</div>
      </div>
    );
  }

  if (error || !recorrido) {
    return (
      <div className="main-content">
        <div className="container">
          <div className="error">{error || 'Recorrido no encontrado'}</div>
        </div>
      </div>
    );
  }

  if (imagenes.length === 0) {
    return (
      <div className="main-content">
        <div className="container">
          <div className="error">Este recorrido no tiene imágenes panorámicas. Por favor, agrega imágenes desde la página de edición.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="visor-container">
      <div className="visor-header">
        <div className="visor-info">
          <h1>{recorrido.titulo}</h1>
          {recorrido.descripcion && <p>{recorrido.descripcion}</p>}
        </div>
        <div className="visor-actions">
          <button onClick={copiarEnlace} className="btn btn-primary btn-sm">
            Copiar Enlace
          </button>
          <button onClick={descargarCodigo} className="btn btn-success btn-sm">
            Descargar Código Embed
          </button>
        </div>
      </div>

      <div className="visor-wrapper">
        <div 
          ref={viewerRef} 
          id="panorama" 
          className="panorama-viewer"
        ></div>
      </div>

      {imagenes.length > 1 && (
        <div className="visor-navegacion">
          <button
            onClick={() => cambiarImagen(imagenActual - 1)}
            disabled={imagenActual === 0}
            className="btn btn-primary"
          >
            ← Anterior
          </button>
          <span className="visor-contador">
            {imagenActual + 1} / {imagenes.length}
          </span>
          <button
            onClick={() => cambiarImagen(imagenActual + 1)}
            disabled={imagenActual === imagenes.length - 1}
            className="btn btn-primary"
          >
            Siguiente →
          </button>
        </div>
      )}

      {imagenes.length > 1 && (
        <div className="visor-miniaturas">
          {imagenes.map((img, index) => (
            <div
              key={img.id}
              className={`miniatura ${index === imagenActual ? 'activa' : ''}`}
              onClick={() => cambiarImagen(index)}
            >
              <img 
                src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${img.ruta_archivo}`} 
                alt={`Vista ${index + 1}`}
              />
            </div>
          ))}
        </div>
      )}

      {/* Tooltip inline para mostrar información del hotspot directamente sobre él */}
      {hotspotMostrando && hotspotPosition.x > 0 && hotspotPosition.y > 0 && (
        <div 
          className="hotspot-info-tooltip"
          style={{
            position: 'fixed',
            left: `${hotspotPosition.x}px`,
            top: `${hotspotPosition.y - 10}px`,
            transform: 'translateX(-50%) translateY(-100%)',
            zIndex: 10001,
            pointerEvents: 'none',
            display: 'block',
            opacity: 1,
            visibility: 'visible'
          }}
        >
          <div className="hotspot-info-content">
            <button 
              className="hotspot-info-close"
              onClick={(e) => {
                e.stopPropagation();
                setHotspotMostrando(null);
              }}
              aria-label="Cerrar"
              style={{ pointerEvents: 'auto' }}
            >
              ×
            </button>
            <div className="hotspot-info-header">
              <div className="hotspot-info-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
              </div>
              <h4 className="hotspot-info-title">
                {hotspotMostrando.nombre || 'Punto de interés'}
              </h4>
            </div>
            {hotspotMostrando.descripcion && hotspotMostrando.descripcion.trim() && (
              <div className="hotspot-info-body">
                <p className="hotspot-info-description">
                  {hotspotMostrando.descripcion}
                </p>
              </div>
            )}
            {hotspotMostrando.telefono && hotspotMostrando.telefono.toString().trim() && (
              <div className="hotspot-info-body">
                <p className="hotspot-info-description" style={{ marginTop: '8px' }}>
                  <strong>Teléfono:</strong>{' '}
                  <a 
                    href={`tel:${hotspotMostrando.telefono}`} 
                    style={{ color: '#007bff', textDecoration: 'none' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {hotspotMostrando.telefono}
                  </a>
                </p>
              </div>
            )}
            {hotspotMostrando.link && hotspotMostrando.link.toString().trim() && (
              <div className="hotspot-info-body">
                <p className="hotspot-info-description" style={{ marginTop: '8px' }}>
                  <strong>Link:</strong>{' '}
                  <a 
                    href={hotspotMostrando.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ color: '#007bff', textDecoration: 'none', wordBreak: 'break-all' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {hotspotMostrando.link.length > 30 ? `${hotspotMostrando.link.substring(0, 30)}...` : hotspotMostrando.link}
                  </a>
                </p>
              </div>
            )}
            {hotspotMostrando.tipo && (
              <div className="hotspot-info-footer">
                <span className="hotspot-info-badge">
                  {hotspotMostrando.tipo === 'informativo' ? 'ℹ️ Informativo' : hotspotMostrando.tipo === 'navegacion' ? '→ Navegación' : hotspotMostrando.tipo}
                </span>
              </div>
            )}
            {/* Flecha apuntando hacia el hotspot */}
            <div className="hotspot-info-arrow"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Visor360;

